package com.dotdrives.web.ui

import com.dotdrives.controller.*
import com.dotdrives.design.DotColors
import com.dotdrives.model.*
import com.dotdrives.repository.SupabaseWorkflowRepository
import com.dotdrives.repository.WorkflowRepository
import kotlinx.browser.window
import kotlinx.coroutines.*
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import react.*
import react.dom.html.ReactHTML.div
import web.cssom.ClassName

fun useWorkflowController(
    userId: String,
    orgId: String,
    role: String,
    purpose: String = "delivery",
    repositoryFactory: () -> WorkflowRepository = ::SupabaseWorkflowRepository,
): WorkflowController {
    val (holder) =
        useState {
            val scope = MainScope()
            val key = "dotdrives.workflow:$userId:$orgId:$purpose"
            val restored = runCatching {
                window.sessionStorage.getItem(key)?.let {
                    Json.decodeFromString<WorkflowRequest>(it)
                }
            }
            val initial = restored.getOrNull()
            scope to
                WorkflowController(
                    repositoryFactory(),
                    scope,
                    orgId,
                    role,
                    initial,
                    restoreError =
                        if (restored.isFailure)
                            "The saved action could not be read. Restore browser storage access or contact support before starting another action."
                        else null,
                ) { pending ->
                    if (pending == null) window.sessionStorage.removeItem(key)
                    else window.sessionStorage.setItem(key, Json.encodeToString(pending))
                }
        }
    useEffect(holder) {
        try {
            awaitCancellation()
        } finally {
            holder.first.cancel()
        }
    }
    return holder.second
}

external interface WorkflowFeedbackProps : Props {
    var controller: WorkflowController
    var state: WorkflowState
}

val WorkflowFeedback =
    FC<WorkflowFeedbackProps> { props ->
        WorkflowStyles()
        props.state.readErrors.forEach { (key, error) ->
            DotBanner {
                message = error
                onRetry = {
                    props.controller.read(key.substringBefore(':'), key.substringAfter(':'))
                }
            }
        }
        props.state.error?.let { error ->
            DotBanner {
                message = error
                onRetry = if (props.state.pending != null) props.controller::retry else null
            }
        }
        if (props.state.pending != null && props.state.error == null)
            DotButton {
                text = "Retry unfinished action"
                enabled = !(props.state.busy)
                onClick = props.controller::retry
            }
        props.state.message?.let {
            div {
                className = ClassName("workflow-description")
                +it
            }
        }
    }

external interface DeliveryJobsProps : Props {
    var controller: WorkflowController
    var state: WorkflowState
    var sourceId: String?
    var kinds: List<String>?
}

val DeliveryJobs =
    FC<DeliveryJobsProps> { props ->
        WorkflowStyles()
        val jobs =
            props.state.jobs.filter {
                (props.sourceId == null || it.references(props.sourceId!!)) &&
                    (props.kinds == null || it.kind in props.kinds!!)
            }
        div {
            className = ClassName("workflow-card")
            div {
                className = ClassName("workflow-title")
                +"Delivery status"
            }
            div {
                className = ClassName("workflow-description")
                +"Queued messages have not been sent. Provider acceptance does not confirm delivery or opening."
            }
            DotButton {
                text = if (props.state.jobsLoading) "Checking…" else "Refresh delivery status"
                enabled = !(props.state.jobsLoading)
                onClick = props.controller::loadJobs
            }
            props.state.jobsError?.let {
                DotBanner {
                    message = it
                    onRetry = props.controller::loadJobs
                }
            }
            if (jobs.isEmpty() && !props.state.jobsLoading && props.state.jobsError == null)
                div { +"No delivery jobs recorded." }
            jobs
                .sortedByDescending { it.createdAt }
                .forEach { job ->
                    div {
                        key = job.id.unsafeCast<Key>()
                        className = ClassName("workflow-row")
                        div {
                            +"${job.sourceLabel ?: job.kind.removePrefix("email.").replaceFirstChar(Char::uppercase)} · ${job.label}"
                            job.acceptedAt?.let { +" · $it" }
                        }
                        if (job.retryable)
                            DotButton {
                                text = "Retry"
                                enabled = !(props.state.busy)
                                onClick = {
                                    props.controller.perform(
                                        "workflows",
                                        "jobs.retry",
                                        mapOf("job_id" to job.id),
                                    )
                                }
                            }
                    }
                }
        }
    }

/** Delivery cards also mount on Updates, Next Steps and the receipt dialog. */
private val WorkflowStyles =
    FC<Props> {
        react.dom.html.ReactHTML.style {
            +"""
        .workflow-card { background: white; color: ${DotColors.INK}; border: 1px solid ${DotColors.BORDER}; border-radius: 16px; padding: 20px; margin: 18px 0; min-width: 0; overflow-wrap: anywhere; }
        .workflow-title { font-family: 'Quicksand', sans-serif; font-size: 16px; font-weight: 700; margin-bottom: 6px; }
        .workflow-description { color: ${DotColors.TEXT_SECONDARY}; font-size: 13px; line-height: 1.5; margin: 8px 0 14px; }
        .workflow-row { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; padding: 12px 0; border-bottom: 1px solid ${DotColors.BORDER}; }
        .workflow-row:last-child { border-bottom: 0; }
        .workflow-card .dot-button { margin: 6px 8px 6px 0; }
        .workflow-card > div { line-height: 1.5; }
        """
                .trimIndent()
        }
    }
